// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useAuth } from '../../helios/hooks/useAuth';

/** A site the user's organization resolves to, projected for display chrome. */
export interface SharedSite {
  id: string;
  /** Site label as OMS has it, e.g. "GA-10". */
  label: string;
  /** Region, humanized from OMS' region_key slug (e.g. "east-coast" → "East Coast"). */
  region: string;
}

/**
 * "east-coast" / "california" → "East Coast" / "California". OMS' region_key
 * is a slug, not display text — this is a display transform of the real
 * value, not a substitute for one.
 */
function humanizeRegion(regionKey: string): string {
  return regionKey
    .split(/[-_]/)
    .filter(Boolean)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The session view Admin Center is allowed to depend on.
 *
 * This is a deliberately narrow projection of Helios' `useAuth()` — see
 * `shared/README.md` and ADR-0014. Admin Center reads this instead of
 * importing `contexts/AuthContext` or `hooks/useAuth` directly, so the
 * dependency stays a stable, purpose-built contract instead of a reach into
 * Helios' internal shape.
 */
export interface SharedSession {
  /** "First Last", or null while loading / signed out. */
  displayName: string | null;
  /** Two-letter avatar initials derived from displayName, e.g. "DW". */
  initials: string | null;
  email: string | null;
  /** Coarse admin check — Admin Center screens should still gate on `permissions` for anything finer. */
  isAdmin: boolean;
  permissions: string[];
  /** The user's organization id, for scoping cross-org calls (e.g. tenant-scoped user lists). */
  organizationId: string | null;
  /** Number of sites the user's organization resolves to, for chrome like "Fleet · 3 sites". */
  siteCount: number;
  /** The sites themselves, for a scope picker — label + humanized region, no live metrics. */
  sites: SharedSite[];
  isLoading: boolean;
}

function toInitials(firstName?: string, lastName?: string): string | null {
  const first = firstName?.trim()?.[0] ?? '';
  const last = lastName?.trim()?.[0] ?? '';
  const initials = `${first}${last}`.toUpperCase();
  return initials.length > 0 ? initials : null;
}

export function useSharedSession(): SharedSession {
  const { user, permissions, isAdmin, sites: omsSites, isLoading, orgId } = useAuth();

  const displayName = user ? `${user.first_name} ${user.last_name}`.trim() : null;

  return {
    displayName: displayName && displayName.length > 0 ? displayName : null,
    initials: toInitials(user?.first_name, user?.last_name),
    email: user?.email ?? null,
    isAdmin,
    permissions,
    organizationId: orgId,
    siteCount: omsSites.length,
    sites: omsSites.map(site => ({
      id: site.site_id,
      label: site.label,
      region: humanizeRegion(site.region_key),
    })),
    isLoading,
  };
}
